import React, { useCallback, useEffect, useMemo, useState } from "react";

import {
  getAdminInstitutePayments,
  reviewAdminInstitutePayment,
} from "../api/adminclient";

import { apiErrorMessage } from "../api/auth";

const getErrorMessage = (error, fallback) =>
  apiErrorMessage(error, fallback);

const formatMoney = (value) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(Number(value || 0));

const formatDateTime = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en-IN", {
    timeZone: "Asia/Kolkata",
    dateStyle: "medium",
    timeStyle: "short",
  }).format(date);
};

const statusClass = (status) => {
  switch (String(status || "").toUpperCase()) {
    case "PENDING":
      return "bg-warning text-dark";
    case "SUCCESS":
      return "bg-success";
    case "REJECTED":
      return "bg-danger";
    case "FAILED":
      return "bg-danger";
    case "CANCELLED":
      return "bg-secondary";
    default:
      return "bg-light text-dark border";
  }
};

export default function AdminInstitutePayments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [actionError, setActionError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [statusFilter, setStatusFilter] = useState("PENDING");
  const [search, setSearch] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [reviewingPayment, setReviewingPayment] = useState(null);
  const [decision, setDecision] = useState("approve");
  const [adminNote, setAdminNote] = useState("");

  const loadPayments = useCallback(async () => {
    setLoading(true);
    setLoadError("");

    try {
      const response = await getAdminInstitutePayments();
      const data = response?.data;
      setPayments(
        Array.isArray(data)
          ? data
          : Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data?.payments)
              ? data.payments
              : []
      );
    } catch (error) {
      setLoadError(
        getErrorMessage(
          error,
          "Could not load institute payments. Check the Django server, endpoint and admin permissions."
        )
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPayments();
  }, [loadPayments]);

  const filteredPayments = useMemo(() => {
    const query = search.trim().toLowerCase();

    return payments.filter((payment) => {
      const matchesStatus =
        statusFilter === "ALL" ||
        String(payment.status || "").toUpperCase() === statusFilter;

      const registrationText = (payment.registrations || [])
        .map((item) =>
          `${item.student_email || ""} ${item.exam_name || ""} ${item.registration_id || ""}`
        )
        .join(" ");

      const searchable = [
        payment.institute_name,
        payment.submitted_by,
        payment.transaction_reference,
        payment.payment_method,
        payment.id,
        registrationText,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return matchesStatus && (!query || searchable.includes(query));
    });
  }, [payments, statusFilter, search]);

  const pendingCount = payments.filter(
    (payment) => String(payment.status).toUpperCase() === "PENDING"
  ).length;

  const pendingAmount = payments
    .filter((payment) => String(payment.status).toUpperCase() === "PENDING")
    .reduce((sum, payment) => sum + Number(payment.amount || 0), 0);

  const openReview = (payment, nextDecision) => {
    setActionError("");
    setSuccessMessage("");
    setReviewingPayment(payment);
    setDecision(nextDecision);
    setAdminNote("");
  };

  const closeReview = () => {
    if (busyId !== null) return;
    setReviewingPayment(null);
    setAdminNote("");
  };

  const handleReview = async (event) => {
    event.preventDefault();
    if (!reviewingPayment) return;

    if (decision === "reject" && !adminNote.trim()) {
      setActionError("Please enter a reason before rejecting this payment.");
      return;
    }

    const paymentId = reviewingPayment.id;
    setBusyId(paymentId);
    setActionError("");
    setSuccessMessage("");

    try {
      const response = await reviewAdminInstitutePayment({
        paymentId,
        decision,
        adminNote: adminNote.trim(),
      });

      setSuccessMessage(
        response?.data?.message ||
          `Institute payment #${paymentId} ${decision === "approve" ? "approved" : "rejected"}.`
      );
      setReviewingPayment(null);
      setAdminNote("");
      await loadPayments();
    } catch (error) {
      setActionError(
        getErrorMessage(
          error,
          "Could not review this payment. The backend must accept payment_id, decision and admin_note."
        )
      );
    } finally {
      setBusyId(null);
    }
  };

  return (
    <>
      <div className="container-fluid p-0">
          <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
            <div>
              <h2 className="fw-bold mb-1">Institute Payment Approvals</h2>
              <p className="text-muted mb-0">
                Review manual UPI and bank-transfer submissions from institutes.
              </p>
            </div>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={loadPayments}
              disabled={loading}
            >
              <i className="bi bi-arrow-clockwise me-2" />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>

          {successMessage && (
            <div className="alert alert-success" role="status">
              {successMessage}
            </div>
          )}

          {actionError && !reviewingPayment && (
            <div className="alert alert-danger" role="alert">
              {actionError}
            </div>
          )}

          <div className="row g-3 mb-4">
            <div className="col-sm-6 col-xl-4">
              <div className="admin-card p-3 p-md-4 h-100">
                <div className="text-muted small">Pending Submissions</div>
                <div className="fs-3 fw-bold">{pendingCount}</div>
              </div>
            </div>
            <div className="col-sm-6 col-xl-4">
              <div className="admin-card p-3 p-md-4 h-100">
                <div className="text-muted small">Amount Awaiting Review</div>
                <div className="fs-3 fw-bold">{formatMoney(pendingAmount)}</div>
              </div>
            </div>
            <div className="col-sm-6 col-xl-4">
              <div className="admin-card p-3 p-md-4 h-100">
                <div className="text-muted small">All Submissions</div>
                <div className="fs-3 fw-bold">{payments.length}</div>
              </div>
            </div>
          </div>

          <section className="admin-card p-3 p-md-4">
            <div className="row g-3 mb-4">
              <div className="col-md-8">
                <label htmlFor="institute-payment-search" className="form-label">
                  Search Payments
                </label>
                <input
                  id="institute-payment-search"
                  type="search"
                  className="form-control"
                  placeholder="Institute, reference, student, exam or payment ID..."
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                />
              </div>
              <div className="col-md-4">
                <label htmlFor="institute-payment-status" className="form-label">
                  Payment Status
                </label>
                <select
                  id="institute-payment-status"
                  className="form-select"
                  value={statusFilter}
                  onChange={(event) => setStatusFilter(event.target.value)}
                >
                  <option value="PENDING">Pending Verification</option>
                  <option value="SUCCESS">Approved</option>
                  <option value="REJECTED">Rejected</option>
                  <option value="ALL">All Payments</option>
                </select>
              </div>
            </div>

            {loading && <p className="text-muted">Loading institute payments...</p>}

            {!loading && loadError && (
              <div className="alert alert-danger">
                <p className="mb-2">{loadError}</p>
                <button
                  type="button"
                  className="btn btn-outline-danger btn-sm"
                  onClick={loadPayments}
                >
                  Retry
                </button>
              </div>
            )}

            {!loading && !loadError && filteredPayments.length === 0 && (
              <p className="text-muted mb-0">
                {payments.length === 0
                  ? "No institute payment submissions have been received yet."
                  : "No payments match this status or search."}
              </p>
            )}

            {!loading && !loadError && filteredPayments.length > 0 && (
              <div className="table-responsive">
                <table className="table align-middle admin-table">
                  <thead>
                    <tr>
                      <th>Payment</th>
                      <th>Institute / Submitted By</th>
                      <th>Amount</th>
                      <th>Method / Reference</th>
                      <th>Registrations</th>
                      <th>Submitted</th>
                      <th>Status</th>
                      <th className="text-end">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPayments.map((payment) => {
                      const isPending =
                        String(payment.status).toUpperCase() === "PENDING";
                      const busy = busyId === payment.id;

                      return (
                        <tr key={payment.id}>
                          <td className="fw-semibold">#{payment.id}</td>
                          <td>
                            <div className="fw-semibold">
                              {payment.institute_name || "—"}
                            </div>
                            <small className="text-muted">
                              {payment.submitted_by || "—"}
                            </small>
                          </td>
                          <td className="fw-semibold">
                            {formatMoney(payment.amount)}
                          </td>
                          <td>
                            <div>{String(payment.payment_method || "—").replaceAll("_", " ")}</div>
                            <small className="text-muted">
                              {payment.transaction_reference || "No reference"}
                            </small>
                          </td>
                          <td>
                            <div className="small">
                              {(payment.registrations || []).map((item) => (
                                <div key={`${payment.id}-${item.registration_id}`}>
                                  <span className="fw-semibold">
                                    {item.exam_name || "Exam"}
                                  </span>
                                  <span className="text-muted">
                                    {" "}· {item.student_email || "Student"}
                                    {" "}· {formatMoney(item.amount)}
                                  </span>
                                </div>
                              ))}
                              {!(payment.registrations || []).length && "No registrations listed"}
                            </div>
                          </td>
                          <td>{formatDateTime(payment.created_at)}</td>
                          <td>
                            <span className={`badge ${statusClass(payment.status)}`}>
                              {String(payment.status || "UNKNOWN").replaceAll("_", " ")}
                            </span>
                          </td>
                          <td>
                            <div className="d-flex justify-content-end flex-wrap gap-2">
                              {payment.payment_proof && (
                                <div className="d-flex flex-column align-items-end gap-2">
                                  {/\\.(png|jpe?g|webp|gif)(\\?.*)?$/i.test(
                                    String(payment.payment_proof)
                                  ) && (
                                    <a
                                      href={payment.payment_proof}
                                      target="_blank"
                                      rel="noreferrer"
                                      title="Open uploaded transaction proof"
                                    >
                                      <img
                                        src={payment.payment_proof}
                                        alt={`Transaction proof for payment #${payment.id}`}
                                        style={{
                                          display: "block",
                                          width: "130px",
                                          maxHeight: "90px",
                                          objectFit: "contain",
                                          border: "1px solid #dee2e6",
                                          borderRadius: "6px",
                                          background: "#fff",
                                        }}
                                      />
                                    </a>
                                  )}
                                  <a
                                    className="btn btn-sm btn-outline-primary"
                                    href={payment.payment_proof}
                                    target="_blank"
                                    rel="noreferrer"
                                  >
                                    <i className="bi bi-paperclip me-1" />
                                    View transaction proof
                                  </a>
                                </div>
                              )}
                              {isPending && (
                                <>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-success"
                                    disabled={busy}
                                    onClick={() => openReview(payment, "approve")}
                                  >
                                    Approve
                                  </button>
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-outline-danger"
                                    disabled={busy}
                                    onClick={() => openReview(payment, "reject")}
                                  >
                                    Reject
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
      </div>

      {reviewingPayment && (
        <div
          className="modal d-block"
          role="dialog"
          aria-modal="true"
          aria-labelledby="review-institute-payment-title"
          style={{ background: "rgba(0,0,0,0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <form onSubmit={handleReview}>
                <div className="modal-header">
                  <h5 className="modal-title" id="review-institute-payment-title">
                    {decision === "approve" ? "Approve Payment" : "Reject Payment"}
                  </h5>
                  <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={closeReview}
                    disabled={busyId !== null}
                  />
                </div>
                <div className="modal-body">
                  <p>
                    Payment <strong>#{reviewingPayment.id}</strong> from{" "}
                    <strong>{reviewingPayment.institute_name}</strong> for{" "}
                    <strong>{formatMoney(reviewingPayment.amount)}</strong>.
                  </p>
                  {decision === "approve" ? (
                    <div className="alert alert-info">
                      Approving marks its eligible registrations as PAID so the
                      institute can proceed to scheduling.
                    </div>
                  ) : (
                    <div className="alert alert-warning">
                      Rejected payments do not unlock scheduling. Enter a reason
                      for the institute's record.
                    </div>
                  )}
                  <label htmlFor="admin-payment-note" className="form-label">
                    Admin note {decision === "reject" ? "(required)" : "(optional)"}
                  </label>
                  <textarea
                    id="admin-payment-note"
                    className="form-control"
                    rows={3}
                    value={adminNote}
                    onChange={(event) => setAdminNote(event.target.value)}
                    required={decision === "reject"}
                    placeholder={
                      decision === "reject"
                        ? "Explain why this payment is being rejected..."
                        : "Optional note for the payment record..."
                    }
                  />
                  {actionError && (
                    <div className="alert alert-danger mt-3 mb-0">
                      {actionError}
                    </div>
                  )}
                </div>
                <div className="modal-footer">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={closeReview}
                    disabled={busyId !== null}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={`btn ${decision === "approve" ? "btn-success" : "btn-danger"}`}
                    disabled={busyId !== null}
                  >
                    {busyId !== null
                      ? "Saving..."
                      : decision === "approve"
                        ? "Confirm Approval"
                        : "Confirm Rejection"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
